import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes, useParams } from "react-router-dom";
import { EmptyState, ToastProvider } from "@/ui";
import { setUnauthorizedHandler } from "@/app/api";
import { endSession } from "@/app/auth";
import { DevOnly } from "@/app/DevOnly";
import { Layout } from "@/app/Layout";
import { RequireAuth } from "@/app/RequireAuth";
import { NAV } from "@/app/nav";
import { PAGES } from "@/pages";
import { BalanceDetail } from "@/routes/BalanceDetail";
import { CaptureDetail } from "@/routes/CaptureDetail";
import { ChannelGroupDetail } from "@/routes/ChannelGroupDetail";
import { LoginPage } from "@/routes/LoginPage";
import { NotFound } from "@/routes/Placeholder";
import { PriceTimelineDetail } from "@/routes/PriceTimelineDetail";
import { AccountRouting } from "@/routes/AccountRouting";

// 按账号身份挂载，站内切换参数不能沿用前一账号草稿。
function AccountRoutingRoute() { const { id } = useParams();return <AccountRouting key={id} />; }

// 流量监控详情用到图表库，单独打包，打开详情时才加载（#236）
const TrafficDetail = lazy(() => import("@/routes/TrafficDetail").then((m) => ({ default: m.TrafficDetail })));

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 10_000, retry: 1, refetchOnWindowFocus: false } },
});

// 任何接口回 401 都说明会话失效：结束本地会话，守卫把人送回登录页，登录后回到原来的页面
setUnauthorizedHandler(() => endSession(queryClient));

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route element={<RequireAuth />}>
              <Route element={<Layout />}>
                <Route index element={<Navigate to="/overview" replace />} />
                {NAV.flatMap((g) => g.items).map((it) => {
                  const Page = PAGES[it.path];
                  return <Route key={it.path} path={it.path} element={it.devOnly ? <DevOnly><Page /></DevOnly> : <Page />} />;
                })}
                <Route path="/prices/:kind/:id" element={<PriceTimelineDetail />} />
                <Route path="/account-routing/:id" element={<AccountRoutingRoute />} />
                <Route path="/captures/:id" element={<CaptureDetail />} />
                <Route path="/channel-groups/:id" element={<ChannelGroupDetail />} />
                <Route path="/balances/:id" element={<BalanceDetail />} />
                <Route path="/traffic/:customerId/:mcc" element={<Suspense fallback={<EmptyState title="加载中" />}><TrafficDetail /></Suspense>} />
                <Route path="*" element={<NotFound />} />
              </Route>
            </Route>
          </Routes>
        </BrowserRouter>
      </ToastProvider>
    </QueryClientProvider>
  );
}
